import { useState } from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';
import { inputLabelSx, labelContainerSx, requiredAsteriskSx, errorMessageSx } from './Input.styles';
import { getTextAreaContainerSx, getTextAreaSx, characterCountSx } from './TextArea.styles';

interface TextAreaProps {
  /**
   * Label text for the textarea field
   */
  label?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Placeholder text for the textarea
   */
  placeholder?: string;
  /**
   * Maximum character length
   */
  maxLength?: number;
  /**
   * Controlled value
   */
  value?: string;
  /**
   * Change handler
   */
  onChange?: (value: string) => void;
  /**
   * Number of rows for the textarea
   */
  rows?: number;
  /**
   * Custom sx styles for the textarea container
   */
  sx?: SxProps<Theme>;
  /**
   * Custom sx styles for the label
   */
  labelSx?: SxProps<Theme>;
  /**
   * Whether to display the character count
   */
  showCharacterCount?: boolean;
  /**
   * Whether the field has an error
   */
  error?: boolean;
  /**
   * Helper text to display below the textarea (typically used for error messages)
   */
  helperText?: string;
}

/**
 * Reusable TextArea component with support for labels, required fields, and character counting
 * 
 * @example
 * ```tsx
 * <TextArea
 *   label="Description"
 *   placeholder="Enter description"
 *   required
 *   maxLength={500}
 * />
 * 
 * <TextArea
 *   label="Comments"
 *   value={comments}
 *   onChange={setComments}
 *   rows={6}
 * />
 * ```
 */
export const TextArea = ({
  label,
  required,
  placeholder,
  maxLength = 750,
  value: controlledValue,
  onChange,
  rows = 4,
  sx,
  labelSx,
  showCharacterCount = true,
  error = false,
  helperText
}: TextAreaProps) => {
  const [internalValue, setInternalValue] = useState('');
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }
    if (onChange) {
      onChange(newValue);
    }
  };

  const remaining = maxLength - value.length;

  const baseContainerSx = getTextAreaContainerSx(error);
  const containerSx = sx
    ? ([baseContainerSx, sx] as SxProps<Theme>)
    : baseContainerSx;

  const mergedLabelSx = labelSx
    ? ([inputLabelSx, labelSx] as SxProps<Theme>)
    : inputLabelSx;

  const textAreaBaseSx = getTextAreaSx();

  return (
    <Box sx={labelContainerSx}>
      {label && (
        <Typography
          sx={mergedLabelSx}
          component="label"
        >
          {label}
          {required && (
            <Box component="span" sx={requiredAsteriskSx}>
              *
            </Box>
          )}
        </Typography>
      )}
      <Box sx={containerSx}>
        <Box
          component="textarea"
          rows={rows}
          value={value}
          onChange={handleChange}
          maxLength={maxLength}
          placeholder={placeholder}
          sx={textAreaBaseSx}
        />

        {showCharacterCount && (
          <Typography sx={characterCountSx}>
            {remaining} characters remaining
          </Typography>
        )}
      </Box>
      {helperText && (
        <Typography sx={errorMessageSx}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};
